import * as DialogPrimitive from '@radix-ui/react-dialog';
import classNames from 'classnames';
import React, { type ReactNode, useCallback, useEffect } from 'react';
import CloseIconButton from '~/components2/forms/CloseIconButton';

interface Props {
  isOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  trigger: ReactNode;
  title: string | ReactNode;
  children: (closeDialog: () => void) => ReactNode;
  contentClassName?: string;
  childrenClassName?: string;
  hasCloseButton?: boolean;
  onEscapeKeyDown?: (event: KeyboardEvent) => void;
  dialogLevel?: number;
}

const Z_INDEX_DEPTH: { [level: number]: number } = {
  1: 100,
  2: 120,
  3: 130,
};

export default function Dialog({
  isOpen,
  onOpenChange,
  title,
  trigger,
  children,
  contentClassName,
  childrenClassName,
  hasCloseButton = true,
  onEscapeKeyDown,
  dialogLevel = 1,
}: Props) {
  const [open, setOpen] = React.useState(isOpen || false);

  useEffect(() => {
    setOpen(!!isOpen);
  }, [isOpen]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  const _onOpenChange = useCallback((isOpen: boolean) => {
    if (onOpenChange && isOpen === undefined) {
      return onOpenChange(isOpen);
    } else if (onOpenChange) {
      onOpenChange(isOpen);
      setOpen(isOpen);
    } else {
      setOpen(isOpen);
    }
  }, []);

  const closeDialog = () => {
    _onOpenChange(false);
  };

  const zOverlay = Z_INDEX_DEPTH[dialogLevel];
  const zContent = zOverlay + 10;

  return (
    <DialogPrimitive.Root open={open} onOpenChange={_onOpenChange}>
      <DialogPrimitive.Trigger asChild>{trigger}</DialogPrimitive.Trigger>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={classNames('data-[state=open]:animate-overlayShow fixed inset-0 bg-black bg-opacity-40')}
          style={{ zIndex: zOverlay }}
        />
        <DialogPrimitive.Content
          className={classNames(
            `fixed left-[50%] top-[50%] ${contentClassName} max-h-full min-w-min translate-x-[-50%] translate-y-[-50%] rounded bg-white p-4 shadow data-[state=open]:animate-contentShow`,
          )}
          style={{
            transform: 'translate(-50%, -50%)',
            zIndex: zContent,
          }}
          onEscapeKeyDown={onEscapeKeyDown}
        >
          {(title || hasCloseButton) && (
            <div className="mb-2 flex items-center justify-between">
              {title && <div className="text-base font-bold">{title}</div>}
              {hasCloseButton && (
                <DialogPrimitive.Close asChild>
                  <CloseIconButton />
                </DialogPrimitive.Close>
              )}
            </div>
          )}

          <div className={classNames(childrenClassName, 'text-sm')}>{children(closeDialog)}</div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

export const DialogContent = ({ children }: { children: ReactNode }) => {
  return <div className="mt-4 max-h-[75vh] max-w-[75vw] text-sm">{children}</div>;
};

export const DialogFooter = ({ children }: { children: ReactNode }) => {
  return <div className="mt-4 flex items-center justify-end gap-2 text-sm">{children}</div>;
};
